/**
 * #282 — the camera pin drawn on every map, and a brand variant for the event popup map.
 * #286 — one colour and one signal (the client, 2026-10-09: "yellow online and a small red dot
 * ... meaning offline"): the pin is yellow, the brand colour; an offline camera carries a small
 * red dot on the top right of its head; a camera with no status (FTP cameras never report in,
 * they only send pictures on motion) carries no dot, because nothing is known to be wrong.
 * #287 — a superuser can change the look for the whole company (lib/mapSettings.ts): the props
 * below carry the shape, colour, size, offline mark and whether the number shows; without them
 * the pin is the item 286 one.
 *
 * Shapes (owner 2026-10-09: "use all shapes" — the six of the comparison page); the outline is
 * 2 px on screen at the medium 40 px width for every shape:
 *   round     a 10.5-unit circle on a short point (viewBox 24 x 30; the owner's pick, the default)
 *   teardrop  the classic pin (viewBox 24 x 36; the item 282–285 shape)
 *   dot       a plain circle (viewBox 24 x 24)
 *   tag       a rounded label on a short point (viewBox 32 x 25.2): the widest room for the number
 *   lollipop  a round head on a stem (viewBox 24 x 36)
 *   camera    the teardrop with a camera drawn in it (viewBox 24 x 36): no number
 * Everything drawn on the pin is dark, one rule (owner 2026-10-09: white on the yellow pin was
 * unreadable and the white border strained the eye): outline slate-800 (#1e293b), camera number
 * and centre dot slate-900 (#0f172a); dark reads at 7:1 or better on every offered colour. The
 * number is extra-bold (800); four digits are drawn smaller and on a higher baseline so the run
 * stays centred on the head.
 *
 * The alert on a pin (index.css, `.detercam-pin-pulse` and its parts) is sized by MapView for every shape
 * and size from alertGeometry() below (#306 ripple): the head's radius, grown ALERT_PIN_SCALE times as the
 * alerting pin is drawn from its tip, is the one measure; the factors are ALERT_FACTORS, nowhere else.
 */
import type { OfflineMark, PinShape } from '@/types'
import { ALERT_PIN_SCALE, SHAPES, type ShapeSpec } from '@/lib/pinGeometry'

export const PIN_WIDTH = 40
export const PIN_HEIGHT = 50
/** The round head on a short point, the tip at the bottom centre. */
export const PIN_PATH = 'M12 1.5a10.5 10.5 0 0 1 10.5 10.5c0 5-3.6 8-7.2 12L12 29.4l-3.3-5.4C5.1 20 1.5 17 1.5 12A10.5 10.5 0 0 1 12 1.5z'
/** The classic teardrop (items 282–285). */
export const TEARDROP_PATH = 'M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z'

/** #286 — the same yellow for every status; the keys stay so callers need no change. */
export const PIN_COLOURS = { online: '#fbbf24', offline: '#fbbf24', unknown: '#fbbf24' } as const
/** #286 — the small dot on an offline pin's head: red-500. */
export const OFFLINE_DOT_COLOUR = '#ef4444'
/** The camera number inside the pin head: slate-900 on every pin colour. */
export const PIN_NUMBER_COLOUR = '#0f172a'
/** The pin outline: slate-800 on every pin colour (was white). */
export const PIN_OUTLINE_COLOUR = '#1e293b'

export function pinColour(isOnline?: boolean | null): string {
  return isOnline === true ? PIN_COLOURS.online : isOnline === false ? PIN_COLOURS.offline : PIN_COLOURS.unknown
}

/** The number tag: a rounded label on a short point (viewBox 32 x 25.2). */
export const TAG_PATH = 'M4 1.5h24a2.5 2.5 0 0 1 2.5 2.5v13a2.5 2.5 0 0 1-2.5 2.5H19.5L16 24.5 12.5 19.5H4A2.5 2.5 0 0 1 1.5 17V4A2.5 2.5 0 0 1 4 1.5z'

// #342 — the shapes' numbers (ShapeSpec, SHAPES) live in lib/pinGeometry.ts, so a lib can read where a pin's head is.

/** The outline's width in viewBox units: 2 px on screen at the medium 40 px width whatever the viewBox width
 *  (the tag is 32 units wide). */
function outlineWidth(spec: ShapeSpec): number {
  return Math.round(1.2 * spec.w / 24 * 100) / 100
}

/** Pixels from the top of a pin drawn `width` wide to the middle of its head. */
export function pinHeadCentre(shape: PinShape, width: number): number {
  const spec = SHAPES[shape]
  return Math.round((spec.head.cy * width) / spec.w)
}

/** The height on screen of a pin drawn `width` wide (the height follows the shape). */
export function pinHeight(shape: PinShape, width: number): number {
  const spec = SHAPES[shape]
  return Math.round((width * spec.h) / spec.w)
}

/** #306 — an alerting pin is drawn this many times larger, from its tip; #342 — the number lives in lib/pinGeometry. */
export { ALERT_PIN_SCALE }

/** #306 review — pixels from the top of a pin's box to the middle of its head while it alerts (the alert
 *  rings' centre): the image grows from its tip, so every point above the tip moves up. */
export function alertHeadCentre(shape: PinShape, width: number): number {
  const height = pinHeight(shape, width)
  return height - (height - pinHeadCentre(shape, width)) * ALERT_PIN_SCALE
}

/** #306 ripple — pixels from the middle of a pin's head to the head's edge, the outline included, drawn `width`
 *  wide and at rest: the farthest point of the head body from its centre (for the tag, a rounded corner). */
export function pinHeadRadius(shape: PinShape, width: number): number {
  const spec = SHAPES[shape]
  return ((spec.head.r + outlineWidth(spec) / 2) * width) / spec.w
}

/** #306 ripple (owner click-through 2026-10-12: "properly designed according to sizes of map pins so have
 *  consistency") — the one place for the alert's proportions. `ring`, `glow` and `box` are multiples of the
 *  grown head's diameter (2 × pinHeadRadius × ALERT_PIN_SCALE); `ringBorder`, `rippleBorder` and the two
 *  `bounce` heights are shares of the pin width. Ripple round 2 (the owner's second look on stage, 2026-10-12:
 *  the ripple read as a few seconds' flicker under the glow, the ring as a thick band on the head): the ring
 *  sits further out (1.55, was 1.4) and is thinner (width / 12, was / 8) with a RING_EDGE dark edge; the ripple
 *  starts just outside the ring, not on the head's edge, so it runs on the map and not on the amber glow, and
 *  the box is wider (2.6, was 2.4) so it still travels a full head of diameter; its line is width / 10 (was / 8:
 *  thinner with the ring, so the ripple is never the heavier of the two). MapView sets every
 *  size inline from alertGeometry(); index.css sizes nothing (the bubble's waves use the same box factor, see there). */
export const ALERT_FACTORS = {
  ring: 1.55,
  glow: 2.25,
  box: 2.6,
  ringBorder: 1 / 12,
  rippleBorder: 1 / 10,
  bounce: [0.35, 0.15],
} as const

/** #306 ripple round 2 — the dark edge drawn on each side of the steady ring (index.css `.detercam-pin-halo`
 *  box-shadow, and the bubble's second shadow), px. The ripple's first frame sits just outside it (its outer
 *  edge; the ripple's line, drawn inside that edge, is born over the ring's edge, as if the ring sent it). */
export const RING_EDGE = 1

export interface AlertGeometry {
  /** pixels from the top of the pin's box to the centre of the alert: the grown head's centre (alertHeadCentre) */
  top: number
  /** the alert box, the ripple's last frame (px, whole) */
  box: number
  /** the steady ring's diameter and border (px, whole) */
  ring: number
  ringBorder: number
  /** the glow's diameter (px, whole) */
  glow: number
  /** the ripple's border (px, whole) and its first frame, just outside the ring's dark edge, as a share of the
   *  box (CSS `--alert-ripple-from`) */
  rippleBorder: number
  rippleFrom: number
  /** the bounce heights, first and second hop (px, whole; CSS `--alert-bounce-1` / `-2`) */
  bounce: [number, number]
}

/** #306 ripple — every size of a pin's alert, for its shape and width. */
export function alertGeometry(shape: PinShape, width: number): AlertGeometry {
  const head = 2 * pinHeadRadius(shape, width) * ALERT_PIN_SCALE
  const box = Math.round(ALERT_FACTORS.box * head)
  const ring = Math.round(ALERT_FACTORS.ring * head)
  return {
    top: alertHeadCentre(shape, width),
    box,
    ring,
    ringBorder: Math.round(ALERT_FACTORS.ringBorder * width),
    glow: Math.round(ALERT_FACTORS.glow * head),
    rippleBorder: Math.round(ALERT_FACTORS.rippleBorder * width),
    rippleFrom: Number(((ring + 2 * RING_EDGE) / box).toFixed(4)),
    bounce: [Math.round(ALERT_FACTORS.bounce[0] * width), Math.round(ALERT_FACTORS.bounce[1] * width)],
  }
}

export function CameraPinSvg({
  isOnline,
  number,
  brand = false,
  label,
  shape = 'round',
  colour = PIN_COLOURS.online,
  width = PIN_WIDTH,
  offlineMark = 'red_dot',
  showNumber = true,
}: {
  isOnline?: boolean | null
  /** The camera number to draw in the head (see lib/cameraNumber); null = the centre dot. */
  number?: string | null
  /** The brand colour instead of a status colour (the event popup's "event location" pin). */
  brand?: boolean
  /** Accessible name ("{name}, {status}"): the SVG becomes an image with this label; without one
   *  it is hidden from assistive tech (the event map's marker carries its own title). The role
   *  sits on the pin itself, not on the marker content: an image role makes every descendant
   *  presentational, which would hide the hover card and the click popup (Plus Code copy button). */
  label?: string
  /** #287 — from the company's map settings. */
  shape?: PinShape
  /** #287 — the pin colour as a hex value; ignored for the brand pin. */
  colour?: string
  /** #287 — width on screen; the height follows the shape. */
  width?: number
  /** #287 — how an offline camera is marked; the brand pin never carries a mark. */
  offlineMark?: OfflineMark
  /** #287 — false draws the centre dot instead of the number. */
  showNumber?: boolean
}) {
  const spec = SHAPES[shape]
  const offline = !brand && isOnline === false
  const fill = brand ? undefined : offline && offlineMark === 'red_pin' ? OFFLINE_DOT_COLOUR : colour
  const text = showNumber ? number : null
  const wide = !!text && text.length >= 4
  const body = {
    fill,
    className: brand ? 'fill-primary' : undefined,
    stroke: PIN_OUTLINE_COLOUR,
    strokeWidth: String(outlineWidth(spec)),
  }
  return (
    <svg
      data-camera-pin
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${spec.w} ${spec.h}`}
      width={width}
      height={pinHeight(shape, width)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {shape === 'dot' ? (
        <circle data-pin-body cx="12" cy="12" r="10.6" {...body} />
      ) : shape === 'lollipop' ? (
        <>
          <path data-pin-stem d="M12 19v15" stroke={PIN_OUTLINE_COLOUR} strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="34" r="1.6" fill={PIN_OUTLINE_COLOUR} />
          <circle data-pin-body cx="12" cy="10" r="9.3" {...body} />
        </>
      ) : (
        // a round join: a mitred join at a sharp tip would reach past the box and be clipped
        <path d={shape === 'round' ? PIN_PATH : shape === 'tag' ? TAG_PATH : TEARDROP_PATH} strokeLinejoin="round" {...body} />
      )}
      {shape === 'camera' ? (
        // the camera icon has no room for a number; the camera itself is the mark
        <g data-camera-glyph>
          <rect x="6" y="9.2" width="12" height="8" rx="1.6" fill={PIN_NUMBER_COLOUR} />
          <rect x="9.2" y="7.2" width="5.6" height="2.4" rx="0.8" fill={PIN_NUMBER_COLOUR} />
          <circle cx="12" cy="13.2" r="2.6" fill={brand ? undefined : fill} className={brand ? 'fill-primary' : undefined} />
          <circle cx="12" cy="13.2" r="1.3" fill={PIN_NUMBER_COLOUR} />
        </g>
      ) : text ? (
        // Dark on the pin, no outline: one rule, readable on every offered colour and on any map.
        <text
          x={spec.head.cx}
          y={wide ? spec.numberY[1] : spec.numberY[0]}
          textAnchor="middle"
          fontSize={wide ? spec.numberSize[1] : spec.numberSize[0]}
          fontWeight="800"
          fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
          fill={PIN_NUMBER_COLOUR}
        >
          {text}
        </text>
      ) : (
        <circle cx={spec.head.cx} cy={spec.head.cy} r="4.5" fill={PIN_NUMBER_COLOUR} />
      )}
      {/* #286 — offline: a small red dot on the top right of the head, like a badge */}
      {offline && offlineMark === 'red_dot' && (
        <circle data-offline-dot cx={spec.dot.cx} cy={spec.dot.cy} r="3.6" fill={OFFLINE_DOT_COLOUR} stroke={PIN_OUTLINE_COLOUR} strokeWidth="1" />
      )}
    </svg>
  )
}
